import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

/**
 * Foreign-key combobox for single-entry ingestion forms.
 *
 * - Debounced search against GET /api/admin/archive/suggest.
 * - Empty query shows recent entries (from backend).
 * - If `allowCreate` and the typed text has no exact label match, appends a
 *   "+ Create new X: '...'" option. Caller decides how to send that to POST
 *   (e.g. send `{song: "..."}` instead of `{song_id: 42}`).
 *
 * Value shape:
 *   null                             → nothing selected
 *   { id, label }                    → existing row picked
 *   { createWith: "..." }            → user opted to create new (text only)
 */
export default function FkCombobox({
  type,
  value,
  onChange,
  allowCreate = false,
  placeholder = '',
  apiUrl,
  authHeaders,
  disabled = false,
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  const fetchSuggestions = useCallback(
    async (q) => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      try {
        const res = await axios.get(`${apiUrl}/api/admin/archive/suggest`, {
          params: { type, q },
          headers: authHeaders ? authHeaders() : undefined,
          signal: controller.signal,
        });
        setResults(Array.isArray(res.data?.results) ? res.data.results : []);
        setHighlight(0);
      } catch (err) {
        if (!axios.isCancel(err)) {
          setResults([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [apiUrl, authHeaders, type],
  );

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(query.trim()), 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open, fetchSuggestions]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const exactMatch = results.find(
    (r) => r.label.toLowerCase() === query.trim().toLowerCase(),
  );
  const showCreateOption =
    allowCreate && query.trim().length > 0 && !exactMatch;
  const options = showCreateOption
    ? [...results, { __create: true, label: `+ Create new: "${query.trim()}"` }]
    : results;

  const selectOption = (opt) => {
    if (opt.__create) {
      onChange({ createWith: query.trim() });
    } else {
      onChange({ id: opt.id, label: opt.label });
    }
    setQuery('');
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (options[highlight]) {
        e.preventDefault();
        selectOption(options[highlight]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const clearSelection = () => {
    onChange(null);
    setQuery('');
  };

  if (value) {
    return (
      <div className="FkCombobox" ref={rootRef}>
        <div className="FkCombobox-selected">
          <span className="FkCombobox-selectedLabel">
            {value.createWith
              ? `New: "${value.createWith}"`
              : value.label}
          </span>
          {!disabled && (
            <button
              type="button"
              className="FkCombobox-clearBtn"
              onClick={clearSelection}
              aria-label="Clear selection"
            >
              ×
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="FkCombobox" ref={rootRef}>
      <input
        type="text"
        className="AdminPortal-input"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
      />
      {open && (
        <ul className="FkCombobox-list" role="listbox">
          {loading && <li className="FkCombobox-hint">Searching…</li>}
          {!loading && options.length === 0 && (
            <li className="FkCombobox-hint">
              {query.trim() ? 'No matches' : 'Start typing to search'}
            </li>
          )}
          {options.map((opt, i) => (
            <li
              key={opt.__create ? 'create' : `${opt.id}-${opt.label}`}
              role="option"
              aria-selected={i === highlight}
              className={`FkCombobox-item${
                i === highlight ? ' FkCombobox-item--active' : ''
              }${opt.__create ? ' FkCombobox-item--create' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                selectOption(opt);
              }}
              onMouseEnter={() => setHighlight(i)}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
